<template>
  <div class="realtime-content">
    <div class="page-header">
      <div>
        <h2 class="page-title">실시간 심박 모니터</h2>
        <p class="page-subtitle">
          수업 예약자를 Polar 기기에 배정하고 실시간 심박수를 모니터링합니다.
        </p>
      </div>
      <div class="service-status" :class="{ ok: localHealth.ok }">
        {{ localHealth.ok ? "로컬 서비스 연결됨" : "로컬 서비스 확인 필요" }}
      </div>
    </div>

    <div class="grid">
      <!-- 1. 수업 선택 -->
      <section class="panel">
        <div class="panel-header">
          <h3>1. 수업 선택</h3>
          <button
            class="subtle-btn"
            @click="refreshAll"
            :disabled="loadingSchedule"
          >
            새로고침
          </button>
        </div>
        <div class="week">
          <button
            v-for="(d, i) in days"
            :key="d.date"
            :class="['day', { sel: i === selectedIdx }]"
            @click="selectDay(i)"
          >
            <span class="dow">{{ d.dow }}</span>
            <span class="num">{{ d.num }}</span>
          </button>
        </div>
        <div class="class-list">
          <button
            v-for="cls in schedule"
            :key="cls.id"
            class="class-item"
            :class="{
              active: selectedClass?.id === cls.id,
              completed: cls.hasWorkouts,
            }"
            @click="selectClass(cls)"
          >
            <span>{{ cls.time }} {{ typeLabelOf(cls) }}</span>
            <span class="class-meta">
              <span v-if="cls.hasWorkouts" class="done-badge">진행됨</span>
              {{ cls.booked }} / {{ cls.capacity }}명
            </span>
          </button>
          <div
            v-if="!loadingSchedule && schedule.length === 0"
            class="empty-box"
          >
            해당 날짜 수업이 없습니다.
          </div>
        </div>
      </section>

      <!-- 2. 참가자 / 기기 배정 -->
      <section class="panel">
        <div class="panel-header">
          <h3>2. 참가자 / 기기 배정</h3>
          <div class="header-actions">
            <button
              class="subtle-btn"
              @click="autoAssign"
              :disabled="!participants.length"
            >
              자동 배정
            </button>
            <button
              class="subtle-btn"
              @click="showAddMemberModal = true"
              :disabled="!selectedClass"
            >
              회원 추가
            </button>
            <button
              class="subtle-btn"
              @click="showAddGuestModal = true"
              :disabled="!selectedClass"
            >
              게스트 추가
            </button>
          </div>
        </div>

        <div
          v-if="
            selectedClass &&
            !sessionRunning &&
            !sessionResult &&
            recoverableSessions.length > 0
          "
          class="recovery-banner"
        >
          <div class="recovery-text">
            ⚠️ 저장되지 않은 이전 세션이
            {{ recoverableSessions.length }}개 있습니다
          </div>
          <button
            v-for="(s, idx) in recoverableSessions"
            :key="s.filename"
            class="primary-btn"
            @click="restoreSession(idx)"
          >
            {{ formatRecoveryLabel(s) }} 복구
          </button>
        </div>

        <div v-if="!selectedClass" class="empty-box">
          수업을 먼저 선택해주세요.
        </div>
        <div v-else-if="loadingBookings" class="empty-box">
          예약자를 불러오는 중...
        </div>
        <div v-else-if="participants.length === 0" class="empty-box">
          참가자가 없습니다.
        </div>

        <div v-else class="booking-list">
          <div v-for="p in participants" :key="p.id" class="booking-card">
            <div class="booking-info">
              <div class="booking-name">
                {{ p.name }}
                <span v-if="p.isGuest" class="guest-badge">{{
                  p.slot_name || "게스트"
                }}</span>
              </div>
              <div v-if="p.guest_phone" class="booking-phone">
                {{ p.guest_phone }}
              </div>
              <div class="booking-meta">
                최대심박 {{ p.max_hr || "-" }} / {{ p.gender || "-" }}
                <button
                  v-if="p.needsGuestInfo"
                  class="guest-info-btn"
                  @click="openGuestInfoModal(p)"
                >
                  정보 입력
                </button>
              </div>
            </div>
            <select
              class="device-select"
              v-model="p.device_no"
              @change="saveMappingToLocal()"
            >
              <option :value="0">기기 선택</option>
              <option
                v-for="d in devices"
                :key="d.device_no"
                :value="d.device_no"
                :disabled="
                  usedDeviceNos.has(d.device_no) && p.device_no !== d.device_no
                "
              >
                #{{ d.device_no
                }}{{
                  usedDeviceNos.has(d.device_no) && p.device_no !== d.device_no
                    ? " (사용중)"
                    : ""
                }}
              </option>
            </select>
            <button
              v-if="!sessionRunning"
              class="icon-btn danger"
              @click="removeParticipant(p)"
              title="제거"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="action-row">
          <button
            v-if="!sessionRunning && !classExpired"
            class="primary-btn"
            @click="startSession"
            :disabled="!canStartSession"
          >
            {{
              selectedClass?.hasWorkouts
                ? "다시 시작 (이전 기록 삭제)"
                : "수업 시작"
            }}
          </button>
          <span v-if="classExpired" class="expired-text">
            수업 종료시간이 지났습니다
          </span>
          <span v-if="boardOverflow" class="expired-text">
            TV 보드는 12명까지만 표시됩니다
          </span>
          <button
            v-if="sessionRunning"
            class="subtle-btn"
            @click="selectClass(selectedClass)"
            :disabled="loadingBookings"
            title="수업 시작 후 새로 예약한 회원을 목록에 불러옵니다"
          >
            {{ loadingBookings ? "불러오는 중..." : "예약자 새로고침" }}
          </button>
          <button
            v-if="sessionRunning && !sessionForOtherClass"
            class="subtle-btn"
            @click="doSync"
            :disabled="syncing"
            title="기기 배정 변경(추가·제거 포함)을 진행 중인 세션에 반영합니다"
          >
            {{ syncing ? "동기화 중..." : "동기화" }}
          </button>
          <button
            v-if="sessionRunning && !sessionForOtherClass"
            class="danger-btn"
            @click="confirmStop"
            :disabled="stoppingSession"
          >
            {{ stoppingSession ? "종료 중..." : "수업 종료" }}
          </button>
          <span v-if="sessionForOtherClass" class="expired-text">
            다른 수업({{ sessionSnapshot?.class_time }})의 세션이 진행 중입니다
            — 그 수업을 선택해야 동기화·종료할 수 있습니다
          </span>
        </div>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </section>
    </div>

    <!-- 3. 실시간 현황 -->
    <section class="panel live-panel">
      <div class="panel-header">
        <h3>3. 실시간 현황</h3>
        <span class="hint">{{
          sessionRunning
            ? `진행 ${formatDuration(sessionSnapshot.elapsed_sec || 0)}`
            : "대기 중"
        }}</span>
      </div>
      <div v-if="!sessionRunning" class="empty-box">
        수업 시작 후 실시간 심박수가 표시됩니다.
      </div>
      <div v-else class="live-grid">
        <article
          v-for="sp in sessionSnapshot.participants"
          :key="sp.participant_id"
          class="live-card"
          :class="'zone-' + (sp.current_zone?.zone ?? 0)"
        >
          <div class="live-name">{{ sp.name }}</div>
          <div class="live-hr">
            {{ sp.current_hr || "--" }} <span class="unit">bpm</span>
          </div>
          <div class="live-percent">
            {{
              sp.current_hr_percent
                ? Math.round(sp.current_hr_percent) + "%"
                : "--%"
            }}
          </div>
          <div class="live-zone">
            {{ sp.current_zone?.label || "대기 중" }}
          </div>
          <div class="live-status">
            {{ sp.connected ? "연결됨" : "연결 끊김" }}
          </div>
        </article>
      </div>
    </section>

    <!-- 종료 확인 팝업 -->
    <div
      v-if="showStopConfirm"
      class="modal-overlay"
      @click.self="showStopConfirm = false"
    >
      <div class="modal">
        <h3>수업을 종료하시겠습니까?</h3>
        <p class="modal-desc">종료 후 운동기록 저장 화면이 표시됩니다.</p>
        <div class="action-row">
          <button class="danger-btn" @click="doStopSession">종료</button>
          <button class="subtle-btn" @click="showStopConfirm = false">
            취소
          </button>
        </div>
      </div>
    </div>

    <!-- 종료 결과 + 체크박스 확인 -->
    <section v-if="sessionResult" class="panel result-panel">
      <div class="panel-header">
        <h3>종료 결과</h3>
      </div>
      <div class="result-list">
        <div
          v-for="r in sessionResultWithCheck"
          :key="r.participant_id"
          class="result-item"
        >
          <label class="result-check">
            <input
              type="checkbox"
              v-model="r.checked"
              :disabled="saveDone || !r.user_id || !r.booking_id"
            />
          </label>
          <div class="result-info">
            <div class="result-name">
              {{ r.name }}
              <span v-if="r.is_guest" class="guest-badge"
                >게스트 - 로컬만 저장</span
              >
              <span v-if="r.user_id && !r.booking_id" class="guest-badge"
                >예약 없음 - 저장 불가</span
              >
            </div>
            <div class="result-meta">
              평균 {{ r.avg_hr }}bpm / 최대 {{ r.max_hr }}bpm /
              {{ r.calories }}kcal / {{ Math.round(r.duration / 60) }}분
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="sessionClassMismatch"
        class="recovery-text"
        style="margin-bottom: 8px"
      >
        ⚠️ 이 결과는 {{ sessionResult.class_time }} 수업 세션입니다 — 저장하면
        그 수업 기록으로 들어갑니다 (현재 선택: {{ selectedClass?.time }})
      </div>
      <div class="action-row" v-if="!saveDone">
        <button class="primary-btn" @click="doImportWorkouts">
          체크된 회원만 저장
        </button>
      </div>
      <div class="import-status">
        <span v-if="importStatus === 'saving'">cloudapi에 저장 중...</span>
        <span v-else-if="importStatus === 'done'" class="success-text"
          >저장 완료 ({{ importResult?.created || 0 }}건)<template
            v-if="importResult?.skipped"
          >
            / 건너뜀 {{ importResult.skipped }}건</template
          ><template v-if="importResult?.errored">
            / 거부 {{ importResult.errored }}건 — 아래 표에서 사유
            확인</template
          ><template v-if="importResult?.dropped_no_booking">
            / 예약 없음 {{ importResult.dropped_no_booking }}건 (저장
            불가)</template
          ></span
        >
        <span v-else-if="importStatus === 'error'" class="error-text"
          >저장 실패: {{ importError }}</span
        >
      </div>
    </section>

    <!-- R7 머신 기록(선택, docs/architecture/app-v2/R6b-R7.md 2.4 위치 1): 세션 저장이 끝나면 그 수업의 출석 처리된 회원 기록을 입력한다 -->
    <section
      v-if="machineRecordsReady"
      class="panel machine-panel"
      data-test="machine-records-panel"
    >
      <div class="panel-header">
        <h3>머신 기록 (선택)</h3>
        <router-link class="machine-later" :to="machineRecordsLink"
          >머신 기록 메뉴에서 열기</router-link
        >
      </div>
      <!-- 폼을 열면 같은 안내가 폼 머리에 있다 -->
      <template v-if="!machineFormOpen">
        <p class="machine-note">
          출석 처리한 회원만 입력할 수 있어요. 입력하지 않고 넘어가도 괜찮고,
          나중에 머신 기록 메뉴에서 입력할 수 있어요.
        </p>
        <button class="primary-btn" @click="machineFormOpen = true">
          머신 기록 입력
        </button>
      </template>
      <MachineRecordForm
        v-else
        :class-id="machineClass.id"
        :class-title="machineClass.title"
        :class-label="machineClass.label"
        @dirty-change="machineFormDirty = $event"
      />
    </section>

    <!-- 회원 검색 모달 -->
    <div
      v-if="showAddMemberModal"
      class="modal-overlay"
      @click.self="showAddMemberModal = false"
    >
      <div class="modal">
        <h3>회원 추가</h3>
        <input
          v-model="memberSearchQuery"
          placeholder="이름 또는 전화번호"
          @input="searchMembers"
          class="input"
        />
        <div class="search-results">
          <div
            v-for="m in memberSearchResults"
            :key="m.id"
            :class="[
              'search-item',
              {
                disabled: participants.some((p) => p.user_id === m.id),
              },
            ]"
            @click="
              !participants.some((p) => p.user_id === m.id) &&
                addMemberFromSearch(m)
            "
          >
            {{ m.name }} ({{ m.phone_number }}) - 최대심박
            {{ m.max_hr || "-" }}
            <span
              v-if="participants.some((p) => p.user_id === m.id)"
              class="already-added"
              >(이미 추가됨)</span
            >
          </div>
          <div
            v-if="memberSearchQuery && !memberSearchResults.length"
            class="empty-box"
          >
            결과 없음
          </div>
        </div>
        <button class="subtle-btn" @click="showAddMemberModal = false">
          닫기
        </button>
      </div>
    </div>

    <!-- 게스트 추가 모달 -->
    <div
      v-if="showAddGuestModal"
      class="modal-overlay"
      @click.self="showAddGuestModal = false"
    >
      <div class="modal">
        <h3>게스트 추가</h3>
        <div class="form-group">
          <label>방문 종류</label>
          <select
            :value="guestForm.visitKind"
            class="input"
            @change="pickGuestVisitKind($event.target.value)"
          >
            <option v-for="k in visitKinds" :key="k.value" :value="k.value">
              {{ k.label }}
            </option>
          </select>
          <p v-if="guestForm.visitNote" class="visit-note">
            {{ guestForm.visitNote }}
          </p>
        </div>
        <div class="form-group">
          <label>무료체험 슬롯</label>
          <select v-model="guestForm.trial_user_id" class="input">
            <option value="">선택</option>
            <option v-for="t in availableTrialUsers" :key="t.id" :value="t.id">
              {{ t.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>이름</label><input v-model="guestForm.name" class="input" />
        </div>
        <div class="form-group">
          <label>전화번호</label
          ><input
            v-model="guestForm.phone"
            class="input"
            placeholder="01012345678"
            @input="onGuestPhone"
          />
        </div>
        <div class="form-group">
          <label>나이</label
          ><input v-model.number="guestForm.age" type="number" class="input" />
        </div>
        <div class="form-group">
          <label>성별</label>
          <select v-model="guestForm.gender" class="input">
            <option value="">선택</option>
            <option value="male">남성</option>
            <option value="female">여성</option>
          </select>
        </div>
        <div class="form-group">
          <label>몸무게 (kg)</label
          ><input
            v-model.number="guestForm.weight_kg"
            type="number"
            class="input"
          />
        </div>
        <div class="action-row">
          <button
            class="primary-btn"
            @click="addGuest"
            :disabled="!guestForm.name || !guestForm.phone"
          >
            추가
          </button>
          <button class="subtle-btn" @click="showAddGuestModal = false">
            닫기
          </button>
        </div>
      </div>
    </div>
    <!-- 게스트 정보 입력 모달 -->
    <div
      v-if="showGuestInfoModal"
      class="modal-overlay"
      @click.self="showGuestInfoModal = false"
    >
      <div class="modal">
        <h3>게스트 정보 입력</h3>
        <p class="modal-desc">{{ guestInfoTarget?.name }}의 운동 정보</p>
        <div class="form-group">
          <label>나이</label>
          <input v-model.number="guestInfoForm.age" type="number" />
        </div>
        <div class="form-group">
          <label>성별</label>
          <select v-model="guestInfoForm.gender">
            <option value="">선택</option>
            <option value="male">남성</option>
            <option value="female">여성</option>
          </select>
        </div>
        <div class="form-group">
          <label>체중 (kg)</label>
          <input v-model.number="guestInfoForm.weight_kg" type="number" />
        </div>
        <div class="action-row">
          <button
            class="primary-btn"
            @click="saveGuestInfo"
            :disabled="!guestInfoForm.gender"
          >
            저장
          </button>
          <button class="subtle-btn" @click="showGuestInfoModal = false">
            닫기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getLocalHrHealth,
  getLocalDevices,
  getCurrentSession,
  startLocalSession,
  stopLocalSession,
  syncSession,
  getSessionsByClass,
  refreshBoard,
} from "@/lib/localHrApi";
import { clampLevelForRealtime } from "@/lib/realtimeLevel";
import { classDisplayName } from "@/lib/classTypes";
import { float4Number } from "@/lib/float4";
import { VISIT_KINDS, applyVisitHistory } from "@/lib/guestVisit";
import MachineRecordForm from "@/components/MachineRecordForm.vue";
import { classTitleFromLabel, formatClassLabel } from "@/lib/machineCatalog";

// 게스트 추가 창의 빈 입력. defaultVisitKind는 코치가 종류를 고르지 않았을 때 돌아갈 값
function emptyGuestForm(defaultVisitKind = "trial") {
  return {
    name: "",
    phone: "",
    age: null,
    gender: "",
    weight_kg: null,
    trial_user_id: "",
    visitKind: defaultVisitKind,
    defaultVisitKind,
    visitKindTouched: false,
    visitNote: "",
  };
}

const API_BASE = process.env.VUE_APP_API_BASE || "http://localhost:8080";
const TV_BOARD_MAX = 12;

// "HH:MM:SS" 등을 "HH:MM"으로 맞춘다. 형식이 다르면 빈 문자열
function toHHMM(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(t || "");
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

const MACHINE_DISCARD_MESSAGE =
  "저장하지 않은 머신 기록이 있어요. 입력을 버리고 넘어갈까요?";

const LEVEL_AUTH_ERROR_MESSAGE =
  "로그인이 만료되었습니다. 다시 로그인한 뒤 세션을 시작해 주세요";

// 레벨 조회가 401/403으로 거절됐을 때 던진다. 이때는 기본값 1로 진행하면 안 된다
class LevelAuthError extends Error {
  constructor(status) {
    super(LEVEL_AUTH_ERROR_MESSAGE);
    this.name = "LevelAuthError";
    this.status = status;
  }
}

// 실시간 세션에 보낼 회원 레벨(1..5, clampLevelForRealtime 참조).
// 인증 실패(401/403)는 LevelAuthError를 던지고, 그 밖의 조회 실패 또는 필드 없으면 1
async function fetchLevel(userId) {
  let res;
  try {
    res = await fetch(`${API_BASE}/v1/admin/members/${userId}/dashboard`, {
      credentials: "include",
    });
  } catch {
    return 1;
  }
  if (res.status === 401 || res.status === 403) {
    throw new LevelAuthError(res.status);
  }
  if (!res.ok) return 1;
  try {
    const data = await res.json();
    return clampLevelForRealtime(data?.level);
  } catch {
    return 1;
  }
}

export default {
  name: "RealtimeHrView",
  components: { MachineRecordForm },
  beforeRouteLeave() {
    return this.confirmDiscardMachineRecords();
  },
  data() {
    return {
      localHealth: { ok: false },
      devices: [],
      schedule: [],
      selectedClass: null,
      selectedIdx: 0,
      days: [],
      loadingSchedule: false,
      loadingBookings: false,
      participants: [],
      sessionRunning: false,
      sessionSnapshot: { participants: [] },
      stoppingSession: false,
      syncing: false,
      sessionResult: null,
      sessionResultWithCheck: [],
      importStatus: null,
      importResult: null,
      importError: null,
      recoverableSessions: [],
      errorMessage: "",
      showGuestInfoModal: false,
      guestInfoTarget: null,
      guestInfoForm: { age: null, gender: "", weight_kg: null },
      pollingTimer: null,
      showAddMemberModal: false,
      showAddGuestModal: false,
      showStopConfirm: false,
      memberSearchQuery: "",
      memberSearchResults: [],
      guestForm: emptyGuestForm(),
      visitKinds: VISIT_KINDS,
      visitLookupTimer: null,
      trialUsers: [],
      saveDone: false,
      machineFormOpen: false,
      machineFormDirty: false,
    };
  },
  computed: {
    sessionForOtherClass() {
      return !!(
        this.sessionRunning &&
        this.sessionSnapshot?.class_id &&
        this.selectedClass &&
        this.sessionSnapshot.class_id !== this.selectedClass.id
      );
    },
    sessionClassMismatch() {
      return !!(
        this.sessionResult?.class_id &&
        this.selectedClass &&
        this.sessionResult.class_id !== this.selectedClass.id
      );
    },
    usedDeviceNos() {
      return new Set(
        this.participants.filter((p) => p.device_no > 0).map((p) => p.device_no)
      );
    },
    canStartSession() {
      return (
        this.selectedClass &&
        this.participants.length > 0 &&
        this.participants.some((p) => p.device_no > 0) &&
        !this.sessionRunning &&
        !this.classExpired
      );
    },
    boardOverflow() {
      return (
        this.participants.filter((p) => p.device_no > 0).length > TV_BOARD_MAX
      );
    },
    classExpired() {
      if (!this.selectedClass) return false;
      const date = this.days[this.selectedIdx]?.date;
      if (!date) return false;
      // KST 기준으로 비교
      const endTime = this.selectedClass.end_time || "23:59";
      const endKST = new Date(date + "T" + endTime + "+09:00");
      return new Date() > endKST;
    },
    // 머신 기록을 넣을 수업 = 세션 저장이 들어간 수업(doImportWorkouts의 targetClassId와 같은 규칙).
    // title(수업 타입 표시명, 기타 수업은 관리자가 정한 이름: 이 화면의 typeLabelOf·class_label과 머신 기록 화면
    // MachineRecordsView.typeLabelOf와 같은 classDisplayName, DECISIONS CT-15)은 머신 선택을 기억하는 키, label은 폼 머리 글자
    machineClass() {
      const id = this.sessionResult?.class_id || this.selectedClass?.id;
      if (!id) return null;
      const cls =
        (this.schedule || []).find((c) => c.id === id) ||
        (this.selectedClass?.id === id ? this.selectedClass : null);
      const title = cls
        ? this.typeLabelOf(cls)
        : classTitleFromLabel(this.sessionResult?.class_label);
      const date =
        this.sessionResult?.class_date || this.days[this.selectedIdx]?.date;
      const time = toHHMM(cls?.time || this.sessionResult?.class_time);
      return {
        id,
        title,
        date: /^\d{4}-\d{2}-\d{2}$/.test(date || "") ? date : "",
        label: formatClassLabel(date, time, title),
      };
    },
    machineRecordsReady() {
      return !!(
        this.sessionResult &&
        this.saveDone &&
        this.importStatus === "done" &&
        this.machineClass
      );
    },
    machineRecordsLink() {
      const query = { class_id: this.machineClass?.id || "" };
      if (this.machineClass?.date) query.date = this.machineClass.date;
      return { path: "/machine-records", query };
    },
    availableTrialUsers() {
      // 이미 현재 참가자 중에 사용 중인 무료체험 유저 제외
      const used = new Set(
        this.participants.map((p) => p.user_id).filter(Boolean)
      );
      return this.trialUsers.filter((t) => !used.has(t.id));
    },
  },
  watch: {
    // 다른 세션 결과로 바뀌면 머신 기록 폼을 닫는다
    sessionResult() {
      this.machineFormOpen = false;
      this.machineFormDirty = false;
    },
  },
  mounted() {
    this.buildDays();
    this.refreshAll();
    this.startPolling();
    this.loadTrialUsers();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    // R9 타입 표시명(lib/classTypes.js). 예전 3개 타입은 값 그대로. 기타 수업은 관리자가 정한 이름(classDisplayName, 2026-10-24 결정 B)
    typeLabelOf(cls) {
      return classDisplayName(cls);
    },
    // 머신 기록 폼에 저장하지 않은 입력이 있으면 결과 화면을 떠나기 전에 확인한다
    confirmDiscardMachineRecords() {
      if (!this.machineFormOpen || !this.machineFormDirty) return true;
      return window.confirm(MACHINE_DISCARD_MESSAGE);
    },
    async loadTrialUsers() {
      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/members/search?q=${encodeURIComponent(
            "무료체험"
          )}`,
          { credentials: "include" }
        );
        const data = await res.json();
        this.trialUsers = (data.members || data || [])
          .filter((u) => u.name && u.name.startsWith("무료체험"))
          .sort((a, b) => {
            const na = parseInt(a.name.replace("무료체험", "")) || 0;
            const nb = parseInt(b.name.replace("무료체험", "")) || 0;
            return na - nb;
          });
      } catch {
        this.trialUsers = [];
      }
    },
    nowKST() {
      // 한국 시간 기준 현재 날짜
      const now = new Date();
      const kst = new Date(
        now.getTime() + (9 * 60 + now.getTimezoneOffset()) * 60000
      );
      return kst;
    },
    openGuestInfoModal(p) {
      this.guestInfoTarget = p;
      this.guestInfoForm = {
        age: p.age || null,
        gender: p.gender || "",
        weight_kg: float4Number(p.weight_kg) || null,
      };
      this.showGuestInfoModal = true;
    },
    saveGuestInfo() {
      if (!this.guestInfoTarget) return;
      const p = this.guestInfoTarget;
      p.gender = this.guestInfoForm.gender;
      p.weight_kg = this.guestInfoForm.weight_kg;
      p.age = this.guestInfoForm.age;
      p.max_hr = this.guestInfoForm.age
        ? 220 - this.guestInfoForm.age
        : p.max_hr;
      p.needsGuestInfo = false;
      this.showGuestInfoModal = false;
      this.saveMappingToLocal();
    },
    saveMappingToLocal() {
      if (!this.selectedClass || this.sessionRunning) return;
      const key = `hr-mapping-${this.selectedClass.id}`;
      localStorage.setItem(key, JSON.stringify(this.participants));
    },
    restoreMappingFromLocal(classId) {
      const key = `hr-mapping-${classId}`;
      const saved = localStorage.getItem(key);
      if (!saved) return null;
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    },
    buildDays() {
      const today = this.nowKST();
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
      const dows = ["월", "화", "수", "목", "금", "토", "일"];
      this.days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return {
          date: `${y}-${m}-${day}`,
          dow: dows[i],
          num: d.getDate(),
        };
      });
      this.selectedIdx = (dayOfWeek + 6) % 7;
    },
    selectDay(i) {
      if (!this.confirmDiscardMachineRecords()) return;
      this.selectedIdx = i;
      this.selectedClass = null;
      this.participants = [];
      this.sessionResult = null;
      this.loadSchedule();
    },
    async refreshAll() {
      await Promise.all([
        this.checkHealth(),
        this.loadDevices(),
        this.loadSchedule(),
      ]);
    },
    async checkHealth() {
      try {
        this.localHealth = await getLocalHrHealth();
      } catch {
        this.localHealth = { ok: false };
      }
    },
    async loadDevices() {
      try {
        const res = await getLocalDevices();
        this.devices = res.devices || [];
      } catch {
        this.devices = [];
      }
    },
    async loadSchedule() {
      this.loadingSchedule = true;
      try {
        const date = this.days[this.selectedIdx]?.date;
        const res = await fetch(
          `${API_BASE}/v1/admin/classes/schedule?date=${date}`,
          { credentials: "include" }
        );
        const data = await res.json();
        const classes = (data.classes || []).map((c) => ({
          id: c.id,
          time: c.time,
          end_time: c.end_time || "",
          type: c.type,
          type_label: c.type_label || "",
          title: c.title || "", // 기타 수업 이름(classDisplayName). 다른 타입은 보지 않는다
          booked: c.booked,
          capacity: c.capacity,
          hasWorkouts: false,
        }));
        // 각 수업의 workout 존재 여부 확인
        await Promise.all(
          classes.map(async (cls) => {
            try {
              const r = await fetch(
                `${API_BASE}/v1/admin/classes/${cls.id}/workouts/exists`,
                { credentials: "include" }
              );
              const d = await r.json();
              cls.hasWorkouts = d.exists || false;
            } catch {
              /* ignore */
            }
          })
        );
        this.schedule = classes;
      } catch {
        this.schedule = [];
      }
      this.loadingSchedule = false;
    },
    async selectClass(cls) {
      if (!this.confirmDiscardMachineRecords()) return;
      this.selectedClass = cls;
      this.loadingBookings = true;
      this.sessionResult = null;
      this.sessionResultWithCheck = [];
      this.saveDone = false;
      this.importStatus = null;
      this.recoverableSessions = [];

      // 이미 저장된 운동기록이 없을 때만 로컬 세션 백업 자동 검색
      if (!cls.hasWorkouts) {
        try {
          const data = await getSessionsByClass(cls.id);
          this.recoverableSessions = data?.sessions || [];
        } catch {
          this.recoverableSessions = [];
        }
      }

      // 진행 중인 세션이 있으면 세션 참가자 복원 (예약자 로드는 아래에서 항상 수행)
      let sessionParticipants = null;
      try {
        const snap = await getCurrentSession();
        if (snap.running && snap.class_id === cls.id) {
          this.sessionRunning = true;
          this.sessionSnapshot = snap;
          sessionParticipants = (snap.participants || []).map((sp) => ({
            id: sp.booking_id || sp.participant_id,
            booking_id: sp.booking_id,
            user_id: sp.user_id,
            name: sp.name,
            max_hr: sp.max_hr,
            gender: sp.gender,
            birth_date: sp.birth_date || null,
            weight_kg: float4Number(sp.weight_kg) || null,
            age: sp.age || null,
            device_no: sp.device_no,
            isGuest: sp.is_guest,
            participant_id: sp.participant_id,
          }));
        }
      } catch {
        /* ignore */
      }

      // 예약자 로드 — 세션 진행 중에도 항상 불러와서 새 예약자를 병합한다
      let bookingParticipants = [];
      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/classes/${cls.id}/bookings`,
          { credentials: "include" }
        );
        const data = await res.json();
        bookingParticipants = (data.bookings || [])
          .filter((b) => b.status === "confirmed")
          .map((b) => ({
            id: b.id,
            booking_id: b.id,
            user_id: b.user_id,
            name: b.guest_name || b.user_name,
            slot_name: b.guest_name ? b.user_name : "",
            guest_name: b.guest_name || "",
            guest_phone: b.guest_phone || "",
            max_hr: b.max_hr,
            gender: b.gender,
            birth_date: b.birth_date,
            weight_kg: float4Number(b.weight_kg) || b.weight_kg,
            device_no: 0,
            isGuest: !!b.guest_name,
            needsGuestInfo: !!b.guest_name,
            participant_id: null,
          }));
      } catch {
        bookingParticipants = [];
      }

      if (sessionParticipants) {
        // 세션 참가자 + 세션에 없는 신규 예약자(기기 미배정 상태로 추가,
        // 기기 배정 후 "동기화"를 누르면 세션에 합류)
        const inSession = new Set(
          sessionParticipants.map((p) => p.booking_id).filter(Boolean)
        );
        this.participants = [
          ...sessionParticipants,
          ...bookingParticipants.filter((b) => !inSession.has(b.booking_id)),
        ];
        this.loadingBookings = false;
        return;
      }

      this.participants = bookingParticipants;
      // localStorage에서 기기 배정 복원
      const saved = this.restoreMappingFromLocal(cls.id);
      if (saved) {
        const savedMap = {};
        saved.forEach((s) => {
          if (s.booking_id) savedMap[s.booking_id] = s;
        });
        this.participants.forEach((p) => {
          const s = savedMap[p.booking_id];
          if (!s) return;
          if (s.device_no > 0) p.device_no = s.device_no;
          // 게스트 수동입력(나이·성별·몸무게·최대심박)은 예약 API에 없으므로
          // 저장본에서 되살린다 — 없으면 칼로리가 0으로 계산된다
          if (p.isGuest) {
            if (p.age == null && s.age != null) p.age = s.age;
            if (!p.gender && s.gender) p.gender = s.gender;
            if (p.weight_kg == null && s.weight_kg != null)
              p.weight_kg = s.weight_kg;
            if (!p.max_hr && s.max_hr) p.max_hr = s.max_hr;
            if (s.needsGuestInfo === false) p.needsGuestInfo = false;
          }
        });
        // 저장에만 있던 추가 참가자(게스트 등) 복원
        saved.forEach((s) => {
          if (
            !this.participants.find((p) => p.booking_id === s.booking_id) &&
            s.booking_id
          ) {
            this.participants.push(s);
          }
        });
      }
      this.loadingBookings = false;
    },
    // 참가자 순서대로 level 배열. 게스트(무료체험 포함)와 user_id 없는 참가자는 1.
    // 인증 실패면 null을 돌려준다. 호출자는 오류를 보여주고 시작/동기화를 멈춰야 한다
    async loadLevels(list) {
      try {
        return await Promise.all(
          list.map((p) => (p.user_id && !p.isGuest ? fetchLevel(p.user_id) : 1))
        );
      } catch (e) {
        if (e instanceof LevelAuthError) return null;
        throw e;
      }
    },
    // 수업 종료 시각 "HH:MM". 스케줄에 end_time이 없으면 시작 + 60분
    classEndTimeHHMM() {
      const end = toHHMM(this.selectedClass?.end_time);
      if (end) return end;
      const start = toHHMM(this.selectedClass?.time);
      if (!start) return "";
      const [h, m] = start.split(":").map(Number);
      const total = (h * 60 + m + 60) % (24 * 60);
      return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
        total % 60
      ).padStart(2, "0")}`;
    },
    autoAssign() {
      const allEnabled = this.devices
        .filter((d) => d.enabled)
        .map((d) => d.device_no);
      const alreadyUsed = new Set(
        this.participants.filter((p) => p.device_no > 0).map((p) => p.device_no)
      );
      const available = allEnabled.filter((no) => !alreadyUsed.has(no));
      let idx = 0;
      this.participants.forEach((p) => {
        if (p.device_no > 0) return;
        if (idx < available.length) {
          p.device_no = available[idx];
          idx++;
        }
      });
      this.saveMappingToLocal();
    },
    async doSync() {
      this.syncing = true;
      this.errorMessage = "";
      try {
        if (this.sessionForOtherClass) {
          this.errorMessage =
            "다른 수업의 세션이 진행 중입니다 — 해당 수업에서 동기화하세요";
          return;
        }
        const targets = this.participants.filter((p) => p.device_no > 0);
        if (!targets.length) {
          this.errorMessage =
            "기기가 배정된 참가자가 없어 동기화하지 않았습니다 (전원 해제는 수업 종료로)";
          return;
        }
        const levels = await this.loadLevels(targets);
        if (!levels) {
          this.errorMessage = LEVEL_AUTH_ERROR_MESSAGE;
          return;
        }
        const assigned = targets.map((p, i) => ({
          booking_id: p.booking_id,
          user_id: p.user_id,
          name: p.name,
          device_no: p.device_no,
          max_hr: p.max_hr,
          gender: p.gender,
          birth_date: p.birth_date,
          weight_kg: p.weight_kg,
          age: p.age,
          level: levels[i],
        }));
        await syncSession(assigned, this.sessionSnapshot?.class_id);
        this.errorMessage = "";
      } catch (e) {
        this.errorMessage = "동기화 실패: " + e.message;
      } finally {
        this.syncing = false;
      }
    },
    async addMemberFromSearch(member) {
      // 중복 체크
      if (this.participants.some((p) => p.user_id === member.id)) {
        this.errorMessage = "이미 추가된 회원입니다";
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/classes/${this.selectedClass.id}/bookings`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: member.id }),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const msg =
            err.error?.message || err.message || err.error || "알 수 없는 오류";
          const msgStr = typeof msg === "string" ? msg : JSON.stringify(msg);
          // 멤버십 관련 에러 코드일 때만 게스트 전환 안내
          // (정원 초과·이미 예약·휴회 등 다른 400 에러는 그대로 표시)
          const errCode = err.error?.code || "";
          if (errCode === "MEMBERSHIP_001" || errCode === "MEMBERSHIP_003") {
            if (
              confirm(
                `${member.name}: 멤버십이 없습니다. 게스트로 추가하시겠습니까?`
              )
            ) {
              // 가입한 회원이 수강권 없이 온 것이라 드랍인을 먼저 고른다(코치가 바꿀 수 있다)
              this.guestForm = {
                ...emptyGuestForm("drop_in"),
                name: member.name,
                phone: member.phone_number || "",
                age: member.birth_date
                  ? new Date().getFullYear() -
                    parseInt(member.birth_date.substring(0, 4))
                  : null,
                gender: member.gender || "",
                // REAL(float4) 몸무게가 72.30000305175781로 오므로 되돌린다(앱 전체 리뷰 1차)
                weight_kg: float4Number(member.weight_kg) || null,
                trial_user_id: "",
              };
              this.showAddMemberModal = false;
              this.showAddGuestModal = true;
              applyVisitHistory(
                this.guestForm,
                this.guestForm.phone,
                "drop_in"
              );
            }
            return;
          }
          this.errorMessage = "회원 추가 실패: " + msgStr;
          return;
        }
        const data = await res.json();
        const newMember = {
          id: data.booking?.id || data.id || member.id,
          booking_id: data.booking?.id || data.id,
          user_id: member.id,
          name: member.name,
          max_hr: member.max_hr,
          gender: member.gender,
          birth_date: member.birth_date,
          weight_kg: float4Number(member.weight_kg) || member.weight_kg,
          device_no: 0,
          isGuest: false,
          participant_id: null,
        };
        this.participants.push(newMember);
        this.saveMappingToLocal();
      } catch (e) {
        this.errorMessage = "회원 추가 실패: " + e.message;
      }
      this.showAddMemberModal = false;
      this.memberSearchQuery = "";
      this.memberSearchResults = [];
    },
    async addGuest() {
      const g = this.guestForm;
      if (!g.trial_user_id) {
        this.errorMessage = "무료체험 슬롯을 선택해주세요";
        return;
      }
      if (!g.name || !g.phone) {
        this.errorMessage = "이름과 전화번호를 입력해주세요";
        return;
      }
      try {
        // 전용 무료체험 API 사용: 입력한 이름·전화가 예약(guest_name/guest_phone)에
        // 저장되므로 새로고침해도 무료체험 계정명으로 바뀌지 않는다
        const res = await fetch(
          `${API_BASE}/v1/admin/classes/${this.selectedClass.id}/free-trial`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: g.name,
              phone_number: g.phone,
              trial_user_id: g.trial_user_id,
              send_sms: false,
              visit_kind: g.visitKind,
            }),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          this.errorMessage =
            "무료체험 booking 생성 실패: " +
            (err.error?.message || err.error || res.status);
          return;
        }
        const data = await res.json();
        const newP = {
          id: data.booking?.id || "guest-" + Date.now(),
          booking_id: data.booking?.id,
          user_id: g.trial_user_id,
          name: g.name,
          guest_name: g.name,
          guest_phone: g.phone,
          max_hr: g.age ? 220 - g.age : null,
          gender: g.gender,
          birth_date: null,
          weight_kg: g.weight_kg,
          age: g.age,
          device_no: 0,
          isGuest: true,
          participant_id: null,
        };
        this.participants.push(newP);
        this.saveMappingToLocal();
        this.showAddGuestModal = false;
        this.guestForm = emptyGuestForm();
      } catch (e) {
        this.errorMessage = "게스트 추가 실패: " + e.message;
      }
    },
    pickGuestVisitKind(kind) {
      this.guestForm.visitKind = kind;
      this.guestForm.visitKindTouched = true;
    },
    // 번호를 다 넣으면 참석 기록을 보고, 전에 참석한 번호면 드랍인을 미리 고른다(코치가 고른 종류는 그대로)
    onGuestPhone() {
      clearTimeout(this.visitLookupTimer);
      const form = this.guestForm;
      const defaultKind = form.defaultVisitKind;
      this.visitLookupTimer = setTimeout(
        () => applyVisitHistory(form, form.phone, defaultKind),
        300
      );
    },
    removeParticipant(p) {
      // 세션 시작 전에만 X 버튼이 보이므로 로컬만 제거
      this.participants = this.participants.filter((x) => x.id !== p.id);
      this.saveMappingToLocal();
    },
    async searchMembers() {
      if (this.memberSearchQuery.length < 2) {
        this.memberSearchResults = [];
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/members/search?q=${encodeURIComponent(
            this.memberSearchQuery
          )}`,
          { credentials: "include" }
        );
        const data = await res.json();
        this.memberSearchResults = data.members || [];
      } catch {
        this.memberSearchResults = [];
      }
    },
    async startSession() {
      if (!this.confirmDiscardMachineRecords()) return;
      this.errorMessage = "";
      this.sessionResult = null;
      this.sessionResultWithCheck = [];
      this.saveDone = false;
      this.importStatus = null;

      // 이미 진행된 수업이면 이전 workout 삭제
      if (this.selectedClass?.hasWorkouts) {
        if (!confirm("이전 운동기록이 삭제됩니다. 진행하시겠습니까?")) return;
        try {
          const delRes = await fetch(
            `${API_BASE}/v1/admin/classes/${this.selectedClass.id}/workouts`,
            { method: "DELETE", credentials: "include" }
          );
          if (!delRes.ok) {
            throw new Error(`HTTP ${delRes.status}`);
          }
          this.selectedClass.hasWorkouts = false;
        } catch (e) {
          this.errorMessage =
            "이전 기록 삭제 실패: " +
            e.message +
            " — 세션을 시작하지 않았습니다";
          return;
        }
      }

      const assigned = this.participants.filter((p) => p.device_no > 0);
      if (!assigned.length) {
        this.errorMessage = "기기가 배정된 참가자가 없습니다";
        return;
      }
      try {
        const levels = await this.loadLevels(assigned);
        if (!levels) {
          this.errorMessage = LEVEL_AUTH_ERROR_MESSAGE;
          return;
        }
        const payload = {
          class_id: this.selectedClass.id,
          // R9 새 타입은 표시명(예: Pull & Cardio), 기타 수업은 관리자가 정한 이름(예: 북한산 등산)으로 TV에 보인다
          class_label: `${this.selectedClass.time} ${classDisplayName(
            this.selectedClass
          )}`,
          class_date: this.days[this.selectedIdx].date,
          class_time: this.selectedClass.time,
          class_end_time: this.classEndTimeHHMM(),
          participants: assigned.map((p, i) => ({
            booking_id: p.booking_id,
            user_id: p.user_id,
            name: p.name,
            device_no: p.device_no,
            max_hr: p.max_hr,
            gender: p.gender,
            birth_date: p.birth_date,
            weight_kg: p.weight_kg,
            age: p.age,
            level: levels[i],
          })),
        };
        const snap = await startLocalSession(payload);
        this.sessionRunning = true;
        this.sessionSnapshot = snap;
        if (snap.participants) {
          snap.participants.forEach((sp) => {
            const match = this.participants.find(
              (p) => p.device_no === sp.device_no
            );
            if (match) match.participant_id = sp.participant_id;
          });
        }
      } catch (e) {
        this.errorMessage = "세션 시작 실패: " + e.message;
      }
    },
    confirmStop() {
      this.showStopConfirm = true;
    },
    async doStopSession() {
      this.showStopConfirm = false;
      this.stoppingSession = true;
      try {
        const result = await stopLocalSession(true);
        this.sessionRunning = false;
        this.sessionResult = result;
        this.sessionSnapshot = { participants: [] };
        // 체크박스 초기화: HR 데이터 있으면 체크, 없으면 해제
        this.sessionResultWithCheck = (result.participants || []).map((r) => ({
          ...r,
          checked: r.avg_hr > 0 && !!r.user_id,
        }));
        this.saveDone = false;
      } catch (e) {
        this.errorMessage = "세션 종료 실패: " + e.message;
      }
      this.stoppingSession = false;
    },
    formatRecoveryLabel(s) {
      const session = s.session || {};
      const ended = session.session_ended_at || "";
      const time = ended ? ended.replace("T", " ").slice(0, 19) : s.filename;
      const count = (session.participants || []).length;
      return `${time} (${count}명)`;
    },
    restoreSession(idx) {
      const item = this.recoverableSessions[idx];
      if (!item || !item.session) return;
      const session = item.session;
      this.sessionResult = session;
      this.sessionResultWithCheck = (session.participants || []).map((r) => ({
        ...r,
        checked: r.avg_hr > 0 && !!r.user_id,
      }));
      this.saveDone = false;
      this.importStatus = null;
      this.recoverableSessions = [];
    },
    async doImportWorkouts() {
      if (!this.sessionResult) return;
      this.importStatus = "saving";
      const checked = this.sessionResultWithCheck.filter(
        (r) => r.checked && r.booking_id && r.user_id
      );
      // 체크됐지만 booking_id가 없어 전송에서 빠지는 행 — 조용히 유실 금지
      const droppedNoBooking = this.sessionResultWithCheck.filter(
        (r) => r.checked && r.user_id && !r.booking_id
      ).length;
      if (!checked.length) {
        this.importStatus = "done";
        this.importResult = {
          created: 0,
          dropped_no_booking: droppedNoBooking,
        };
        this.saveDone = droppedNoBooking === 0;
        return;
      }
      // 저장은 화면 선택이 아니라 '세션이 돌던 수업'으로 들어간다
      const targetClassId =
        this.sessionResult.class_id || this.selectedClass?.id;
      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/classes/${targetClassId}/workouts/import`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_started_at: this.sessionResult.session_started_at,
              session_ended_at: this.sessionResult.session_ended_at,
              participants: checked.map((p) => {
                // 무료체험 유저면 guest_name 저장
                const trialUserIds = new Set(this.trialUsers.map((t) => t.id));
                const isTrial = trialUserIds.has(p.user_id);
                return {
                  booking_id: p.booking_id,
                  user_id: p.user_id,
                  guest_name: isTrial ? p.name : "",
                  duration: p.duration,
                  avg_hr: p.avg_hr,
                  max_hr: p.max_hr,
                  calories: p.calories,
                  hr_trace: p.hr_trace,
                  zone_stats: p.zone_stats,
                };
              }),
            }),
          }
        );
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const body = await res.json();
            msg = body.error || body.message || msg;
          } catch {
            /* 본문 없음 */
          }
          throw new Error(msg);
        }
        this.importResult = await res.json();
        if (typeof this.importResult?.created !== "number") {
          throw new Error("서버 응답 형식 오류 (created 없음)");
        }
        this.importResult.dropped_no_booking = droppedNoBooking;
        this.importStatus = "done";
        // 거부·탈락이 하나라도 있으면 저장 버튼을 살려 재시도를 허용한다
        this.saveDone =
          (this.importResult.errored || 0) === 0 && droppedNoBooking === 0;
        const createdAny = (this.importResult.created || 0) > 0;
        if (!createdAny) {
          // 아무것도 안 들어갔으면 UI 상태(hasWorkouts·복구배너)를 건드리지 않는다
        } else if (this.selectedClass?.id === targetClassId) {
          this.selectedClass.hasWorkouts = true;
        } else {
          // 실제 저장된 수업의 상태도 갱신 (복구 배너 재출현 방지)
          const target = (this.schedule || []).find(
            (c) => c.id === targetClassId
          );
          if (target) target.hasWorkouts = true;
        }
        // TV 보드 순위 갱신. 실패해도 저장 결과에는 영향 없음
        try {
          await refreshBoard();
        } catch {
          /* ignore */
        }
      } catch (e) {
        this.importStatus = "error";
        this.importError = e.message;
      }
    },
    startPolling() {
      this.pollingTimer = setInterval(async () => {
        try {
          const snap = await getCurrentSession();
          if (snap.running) {
            this.sessionRunning = true;
            this.sessionSnapshot = snap;
            this._falseCount = 0;
          } else if (this.sessionRunning) {
            this._falseCount = (this._falseCount || 0) + 1;
            if (this._falseCount >= 3) {
              this.sessionRunning = false;
              this.sessionSnapshot = { participants: [] };
              this._falseCount = 0;
            }
          }
        } catch {
          /* ignore - 네트워크 장애 시 상태 유지 */
        }
        try {
          await this.loadDevices();
        } catch {
          /* ignore */
        }
      }, 2000);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    formatDuration(sec) {
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
.realtime-content {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  font-weight: 700;
}
.page-subtitle {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}
.service-status {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  background: #fee;
  color: #c00;
}
.service-status.ok {
  background: #efe;
  color: #060;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
.panel {
  background: #1e1e1e;
  border-radius: 12px;
  padding: 16px;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-header h3 {
  font-size: 16px;
  font-weight: 600;
}
.header-actions {
  display: flex;
  gap: 6px;
}
.hint {
  font-size: 12px;
  color: #888;
}

.subtle-btn {
  background: #333;
  color: #ccc;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.subtle-btn:hover {
  background: #444;
}
.subtle-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.primary-btn {
  background: #49de80;
  color: #000;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}
.primary-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.danger-btn {
  background: #ef4444;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}
.danger-btn.sm {
  padding: 6px 14px;
  font-size: 13px;
}
.danger-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
}
.icon-btn.danger {
  color: #ef4444;
}

.week {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}
.day {
  flex: 1;
  padding: 8px 4px;
  border: 1px solid #333;
  border-radius: 8px;
  background: transparent;
  color: #ccc;
  text-align: center;
  cursor: pointer;
}
.day.sel {
  background: #49de80;
  color: #000;
  border-color: #49de80;
}
.dow {
  display: block;
  font-size: 11px;
}
.num {
  display: block;
  font-size: 16px;
  font-weight: 700;
}

.class-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.class-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  background: #2a2a2a;
  border: 1px solid #333;
  border-radius: 8px;
  color: #ccc;
  cursor: pointer;
}
.class-item.active {
  border-color: #49de80;
  background: #1a2e1a;
}
.class-item.completed {
  border-color: #555;
}
.class-meta {
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 6px;
}
.done-badge {
  background: #555;
  color: #ccc;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
}

.empty-box {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 14px;
}
.error-text {
  color: #ef4444;
  font-size: 13px;
  margin-top: 8px;
}
.success-text {
  color: #49de80;
}
.expired-text {
  color: #888;
  font-size: 13px;
}

.booking-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
}
.booking-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #2a2a2a;
  border-radius: 8px;
}
.booking-info {
  flex: 1;
}
.booking-name {
  font-size: 14px;
  font-weight: 600;
}
.booking-meta {
  font-size: 11px;
  color: #888;
}
.guest-badge {
  background: #f59e0c;
  color: #000;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
}
.booking-phone {
  font-size: 11px;
  color: #808080;
  margin-top: 2px;
}
.guest-info-btn {
  background: #78ff47;
  color: #000;
  border: none;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 8px;
}
.device-select {
  background: #333;
  color: #ccc;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 13px;
}
.action-row {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  align-items: center;
}

.live-panel {
  margin-bottom: 16px;
}
.live-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}
.live-card {
  border-radius: 12px;
  padding: 14px;
  transition: background-color 0.5s ease;
}
.live-name {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 6px;
}
.live-hr {
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
}
.live-hr .unit {
  font-size: 14px;
  font-weight: 400;
  opacity: 0.8;
}
.live-percent {
  font-size: 18px;
  font-weight: 600;
  margin-top: 2px;
}
.live-zone {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 4px;
}
.live-status {
  font-size: 11px;
  margin-top: 6px;
  opacity: 0.7;
}

.zone-0 {
  background: #333;
}
.zone-1 {
  background: #49de80;
  color: #000;
}
.zone-2 {
  background: #24d3ee;
  color: #000;
}
.zone-3 {
  background: #f59e0c;
  color: #000;
}
.zone-4 {
  background: #ef4444;
}
.zone-5 {
  background: #8b5cf6;
}

.result-panel {
  margin-bottom: 16px;
}
.result-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #2a2a2a;
  border-radius: 8px;
}
.result-check input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}
.result-info {
  flex: 1;
}
.result-name {
  font-weight: 700;
  font-size: 14px;
}
.result-meta {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}
.machine-panel {
  margin-bottom: 16px;
}
/* R7 안내 문구는 머신 기록 폼과 같이 모든 폭에서 낱말 사이에서만 줄을 바꾼다(R7 전체 리뷰 4차) */
.machine-note {
  margin: 0 0 12px;
  color: #999;
  font-size: 13px;
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
}
.machine-later {
  color: #49de80;
  font-size: 12px;
}
.cancel-timer {
  font-size: 13px;
}
.import-status {
  margin-top: 10px;
  font-size: 13px;
}
.modal-desc {
  color: #999;
  font-size: 13px;
  margin-bottom: 12px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}
.modal {
  background: #1e1e1e;
  border-radius: 12px;
  padding: 20px;
  width: 400px;
  max-height: 500px;
  overflow-y: auto;
}
.modal h3 {
  margin-bottom: 12px;
}
.input {
  width: 100%;
  background: #333;
  color: #ccc;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  margin-bottom: 8px;
}
.form-group {
  margin-bottom: 8px;
}
.form-group label {
  display: block;
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}
.visit-note {
  margin: 4px 0 0;
  font-size: 12px;
  color: #78ff47;
}
.search-results {
  max-height: 250px;
  overflow-y: auto;
}
.search-item {
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 6px;
  font-size: 13px;
}
.search-item:hover {
  background: #333;
}
.search-item.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.already-added {
  color: #888;
  font-size: 11px;
}

@media (max-width: 768px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

.recovery-banner {
  background: #2a1f10;
  border: 1px solid #ff7a00;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.recovery-text {
  color: #ffb060;
  font-size: 13px;
  font-weight: 600;
}
.recovery-banner button {
  background: #ff7a00;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
}
.recovery-banner button:hover {
  background: #ff8c1a;
}
</style>
